import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { OpanaiService } from '../../services/openai.service';
import { environment } from '../../services/global-environment';
import { GuidService } from '../../services/guid.service';
import { I18nService } from '../../services/i18n.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { MarkdownViewerComponent } from '../../shared/markdown-viewer/markdown-viewer.component';

interface ChatMessage {
  role: string;
  content: string;
}

@Component({
  selector: 'app-ai-customer-service',
  standalone: true,
  imports: [CommonModule, FormsModule, MarkdownViewerComponent, TranslatePipe],
  templateUrl: './ai-customer-service.component.html',
  styleUrl: './ai-customer-service.component.scss'
})
export class AiCustomerServiceComponent implements OnInit, OnDestroy {
  // Modal 狀態
  isModalOpen = false;
  isFullscreen = false;

  // 對話相關
  userMessage = '';
  chatMessages: ChatMessage[] = [];
  isSendingMessage = false;

  // 文字大小控制
  currentFontSize: number = 14; // 預設字體大小 (px)
  minFontSize: number = 12;     // 最小字體大小 (px)
  maxFontSize: number = 20;     // 最大字體大小 (px)
  defaultFontSize: number = 14; // 預設字體大小 (px)

  // AI 設定
  private assistantNo = 'customer-service';
  private companyid = '';
  private responseId = '';

  // 監聽語言變更的訂閱
  private languageChangeSubscription?: Subscription;

  constructor(
    private openAiApiService: OpanaiService,
    private guidService: GuidService,
    private i18nService: I18nService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.companyid = environment.companyid || '';
    this.responseId = "";

    // 監聽語言變更
    this.languageChangeSubscription = this.i18nService.currentLang.subscribe(() => {
      this.initializeGreeting();
      this.updateChatMessagesLanguage();
    });

    // 使用多次嘗試來確保翻譯服務已載入
    this.waitForTranslationsAndInitialize();

    // 添加鍵盤事件監聽
    document.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  // 等待翻譯載入並初始化
  private waitForTranslationsAndInitialize(): void {
    const maxAttempts = 10;
    let attempts = 0;

    const checkAndInit = () => {
      attempts++;
      const greeting = this.i18nService.translate('ai_customer_service.greeting');

      console.log(`AI客服初始化嘗試 ${attempts}: 翻譯結果 = "${greeting}"`);

      if (greeting && greeting !== 'ai_customer_service.greeting') {
        // 翻譯已載入，初始化問候語
        console.log('翻譯服務已載入，初始化問候語');
        this.initializeGreeting();
      } else if (attempts < maxAttempts) {
        // 翻譯尚未載入，100ms 後再試
        setTimeout(checkAndInit, 100);
      } else {
        // 超過最大嘗試次數，使用預設中文問候語
        console.warn('翻譯服務載入超時，使用預設問候語');
        if (this.chatMessages.length === 0) {
          this.chatMessages.push({
            role: 'assistant',
            content: '您好！我是您專屬的 AI 小助手，有什麼可以幫助您的嗎？'
          });
          this.cdr.detectChanges();
        }
      }
    };

    checkAndInit();
  }

  ngOnDestroy(): void {
    // 清除訂閱
    this.languageChangeSubscription?.unsubscribe();
    // 移除鍵盤事件監聽
    document.removeEventListener('keydown', this.handleKeydown.bind(this));
  }

  // 處理鍵盤事件
  private handleKeydown(event: KeyboardEvent): void {
    if (!this.isModalOpen) return;

    if (event.key === 'Escape') {
      if (this.isFullscreen) {
        this.isFullscreen = false;
      } else {
        this.closeModal();
      }
    }
  }

  // 初始化問候語
  private initializeGreeting(): void {
    const greeting = this.i18nService.translate('ai_customer_service.greeting');
    console.log(`initializeGreeting: 翻譯結果 = "${greeting}", 目前訊息數量 = ${this.chatMessages.length}`);

    // 如果還沒有訊息，或第一則訊息不是助手訊息，則初始化
    if (this.chatMessages.length === 0) {
      // 如果翻譯成功（不是返回鍵名），才設置問候語
      if (greeting && greeting !== 'ai_customer_service.greeting') {
        this.chatMessages.push({
          role: 'assistant',
          content: greeting
        });
        console.log('已添加問候語訊息');
        this.cdr.detectChanges();
      }
    } else if (this.chatMessages[0].role !== 'assistant') {
      // 第一則訊息不是助手訊息，插入問候語
      if (greeting && greeting !== 'ai_customer_service.greeting') {
        this.chatMessages.unshift({
          role: 'assistant',
          content: greeting
        });
        console.log('已插入問候語訊息');
        this.cdr.detectChanges();
      }
    }
  }

  // 更新對話訊息的語言
  private updateChatMessagesLanguage(): void {
    // 只更新第一則助手訊息（問候語）
    if (this.chatMessages.length > 0 && this.chatMessages[0].role === 'assistant') {
      const currentContent = this.chatMessages[0].content;
      const knownGreetings = [
        'ai_customer_service.greeting', // 添加翻譯鍵本身
        '您好！我是您專屬的 AI 小助手，有什麼可以幫助您的嗎？',
        'Hello! I\'m your dedicated AI assistant. How can I help you today?',
        'こんにちは！私はあなた専用の AI アシスタントです。何かお手伝いできることはありますか？',
        'สวัสดี! ฉันเป็นผู้ช่วย AI เฉพาะของคุณ มีอะไรที่สามารถช่วยคุณได้บ้าง?',
        'Halo! Saya adalah asisten AI khusus Anda. Adakah yang bisa saya bantu?'
      ];

      // 只有當第一則訊息是問候語時才更新
      if (knownGreetings.includes(currentContent)) {
        const newGreeting = this.i18nService.translate('ai_customer_service.greeting');
        if (newGreeting && newGreeting !== 'ai_customer_service.greeting') {
          this.chatMessages[0].content = newGreeting;
          this.cdr.detectChanges();
        }
      }
    }
  }

  // 開啟/關閉 Modal
  openModal(): void {
    this.isModalOpen = true;
    // 聚焦到輸入框並初始化高度
    setTimeout(() => {
      const inputElement = document.querySelector('.message-input') as HTMLTextAreaElement;
      if (inputElement) {
        inputElement.focus();
        // 初始化輸入框高度
        inputElement.style.height = '50px';
        inputElement.style.overflowY = 'hidden';
      }
      // 初始化字體大小
      this.updateFontSizeVariable();
    }, 100);
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.isFullscreen = false; // 關閉視窗時重設全螢幕狀態
  }

  // 切換全螢幕模式
  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
  }

  // 調整文字大小
  adjustFontSize(action: 'increase' | 'decrease' | 'reset'): void {
    switch (action) {
      case 'increase':
        if (this.currentFontSize < this.maxFontSize) {
          this.currentFontSize += 2;
        }
        break;
      case 'decrease':
        if (this.currentFontSize > this.minFontSize) {
          this.currentFontSize -= 2;
        }
        break;
      case 'reset':
        this.currentFontSize = this.defaultFontSize;
        break;
    }

    // 更新 CSS 變數
    this.updateFontSizeVariable();
  }

  // 更新 CSS 變數
  private updateFontSizeVariable(): void {
    const modalElement = document.querySelector('.modal-container') as HTMLElement;
    if (modalElement) {
      modalElement.style.setProperty('--chat-font-size', `${this.currentFontSize}px`);
    }
  }

  // 發送訊息
  sendMessage(): void {
    if (!this.userMessage.trim() || this.isSendingMessage) {
      return;
    }

    const message = this.userMessage.trim();

    // 加入使用者訊息
    this.chatMessages.push({
      role: 'user',
      content: message
    });

    this.userMessage = '';
    this.isSendingMessage = true;

    // 重設輸入框高度
    setTimeout(() => {
      const textarea = document.querySelector('.message-input') as HTMLTextAreaElement;
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = '50px'; // 重設為最小高度
        textarea.style.overflowY = 'hidden';
      }
    }, 0);

    // 建立 AI 回應的佔位訊息
    const assistantMessage: ChatMessage = {
      role: 'assistant',
      content: ''
    };

    this.chatMessages.push(assistantMessage);
    const assistantIndex = this.chatMessages.length - 1;

    // 滾動到底部
    setTimeout(() => this.scrollToBottom(), 100);


    // 呼叫 OpenAI API
    this.openAiApiService.chatwithOpenAIWithSteam(
      message,
      this.assistantNo,
      this.responseId,
      this.companyid,
      ["mcp_server_CS"], // toolname  mcp_server，裡面只開放 email這個工具
      "vs_685b505891e88191b3eb5581816c612b", // vector_store_id TODO:這個之後要調整成從設定檔來
      [], // imageAttachments
      undefined, // files
      undefined, // file_ids
      'gpt-4o-mini' // model_version
    ).subscribe({
      next: (response) => {
        if (response && response.trim()) {
          try {

            // 嘗試解析 JSON 格式的回應
            const jsonResponse = JSON.parse(response);
            console.log('AI 客服 JSON 回應:', jsonResponse);

            if (jsonResponse.type === 'response.output_text.delta' && jsonResponse.delta) {
              // 處理流式回應的 delta 部分
              this.chatMessages[assistantIndex].content += jsonResponse.delta;
              this.cdr.detectChanges();
              // 動態滾動到底部
              this.scrollToBottom();
            } else if (jsonResponse.type === 'response.done') {
              // 完成回應
              console.log('AI 客服回應完成');
            } else if(jsonResponse.type == 'response.completed' && jsonResponse.response){
                this.responseId = jsonResponse.response.id; //要寫入responseId，才能使用連續對話
                console.log('AI 客服回應完成，responseId:', this.responseId);
            }

            else {
              console.log('未處理的 AI 回應類型:', jsonResponse.type);
            }
          } catch (error) {
            // 如果不是 JSON 格式，直接處理原始文字
            console.log('AI 客服原始文字回應:', response);
            this.chatMessages[assistantIndex].content += response;
            this.cdr.detectChanges();
            // 動態滾動到底部
            this.scrollToBottom();
          }
        }
      },
      error: (error) => {
        console.error('AI 客服回應錯誤:', error);
        this.chatMessages[assistantIndex].content = this.i18nService.translate('ai_customer_service.error_message');
        this.isSendingMessage = false;
        this.cdr.detectChanges();
        this.scrollToBottom();
      },
      complete: () => {
        this.isSendingMessage = false;
        this.cdr.detectChanges();
      }
    });
  }

  // 按 Enter 發送訊息
  onEnterKey(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  // 自動調整輸入框高度
  autoResize(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;

    // 暫時設為 auto 以獲得準確的 scrollHeight
    textarea.style.height = 'auto';

    // 計算新高度，考慮最小和最大高度限制
    const minHeight = 50;   // 最小高度，約 2 行
    const maxHeight = 150;  // 最大高度，約 6-7 行
    const scrollHeight = textarea.scrollHeight;

    // 確保高度在允許範圍內
    let newHeight = Math.max(minHeight, Math.min(maxHeight, scrollHeight));

    // 設定新高度
    textarea.style.height = newHeight + 'px';

    // 處理滾動條顯示
    if (scrollHeight > maxHeight) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }

    // 如果內容為空，重設為最小高度
    if (!textarea.value.trim()) {
      textarea.style.height = minHeight + 'px';
      textarea.style.overflowY = 'hidden';
    }
  }

  // 點擊 Modal 外部關閉（全螢幕模式下不關閉）
  onModalBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget && !this.isFullscreen) {
      this.closeModal();
    }
  }

  // 滾動到對話底部
  private scrollToBottom(): void {
    try {
      const chatContainer = document.querySelector('.chat-messages');
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    } catch (err) {
      console.warn('滾動到底部失敗:', err);
    }
  }
}
